import { Send, Smile } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { sendCommentOfPost } from '../../../api';
import EmojiPopover from '../../emoji-popover/emoji-popover';
import toast from 'react-hot-toast';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { RootState } from '../../../configuration';
import Avatar from '../../avatar/avatar.component';

interface CommentInputComponentProps {
  postId: string;
  refreshCommentClient: () => void;
  userId: string;
  addComment?: (comment: any) => void;
  refreshCurrentPage: () => Promise<void>;
}

const CommentInputComponent = ({
  postId,
  refreshCommentClient,
  userId,
  addComment,
  refreshCurrentPage,
}: CommentInputComponentProps) => {
  const [commentValue, setCommentValue] = useState<string>('');
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const profileUser = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const handleSendComment = async () => {
    if (!commentValue?.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const response = await sendCommentOfPost({
        userId,
        postId,
        content: commentValue.trim(),
      });

      if (response) {
        // Instead of adding comment directly, refresh the current page
        await refreshCurrentPage();
        setCommentValue('');
      }
    } catch (error) {
      toast.error('Không thể gửi bình luận');
    } finally {
      setIsSubmitting(false);
    }
  };

  const emojiBtnRef = useRef<HTMLButtonElement>(null);

  const onEmojiSelect = useCallback((emoji: any) => {
    setCommentValue((prev) => prev + emoji.native);
    setShowEmojiPicker(false);
  }, []);

  return (
    <div className="p-3 border-t border-gray-200 dark:border-zinc-700">
      <div className="flex items-center gap-2">
        {profileUser && (
          <Avatar
            avatar={profileUser.avatar}
            label={profileUser.username}
            size="sm"
          />
        )}
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Thêm bình luận"
            className="w-full bg-gray-100 dark:bg-zinc-800 rounded-full h-10 px-4 py-2 text-sm outline-none pr-10 font-normal text-[#657384]"
            value={commentValue}
            onChange={(e) => setCommentValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendComment();
              }
            }}
          />
          <button
            ref={emojiBtnRef}
            type="button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <Smile size={20} className="text-slate-500" />
          </button>
          <EmojiPopover
            open={showEmojiPicker}
            anchorRef={emojiBtnRef}
            onEmojiSelect={onEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
          />
        </div>
        <button
          onClick={() => handleSendComment()}
          className={`${
            commentValue ? 'text-blue-500 cursor-pointer' : 'text-[#657384]'
          } `}
          disabled={!commentValue}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="40" height="40" rx="20" fill="#EAEFFA" />
            <path
              d="M24.1391 10.96L15.1091 13.96C9.03914 15.99 9.03914 19.3 15.1091 21.32L17.7891 22.21L18.6791 24.89C20.6991 30.96 24.0191 30.96 26.0391 24.89L29.0491 15.87C30.3891 11.82 28.1891 9.61004 24.1391 10.96ZM24.4591 16.34L20.6591 20.16C20.5091 20.31 20.3191 20.38 20.1291 20.38C19.9391 20.38 19.7491 20.31 19.5991 20.16C19.3091 19.87 19.3091 19.39 19.5991 19.1L23.3991 15.28C23.6891 14.99 24.1691 14.99 24.4591 15.28C24.7491 15.57 24.7491 16.05 24.4591 16.34Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CommentInputComponent;
